import React, { useEffect, useState } from 'react';

import { Button, Col, Form, Image, OverlayTrigger, Popover, Row, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import classNames from 'classnames';
import Select from 'react-select';
import Swal from 'sweetalert2';

import { areYouSure } from 'lib/utils/toast';
import {
  BRANDS_FOR_DROPDOWN,
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  GLOBAL_PRODUCT_REMOVE_CATEGORIES,
  GLOBAL_VENDORS_FOR_DROPDOWN,
  PRODUCT_LINKS_SUGGESTIONS_GET,
  PRODUCT_LINKS_SUGGESTIONS_HIDE_SUGGESTIONS,
  PRODUCT_LINKS_SUGGESTIONS_LINK_PRODUCTS,
} from 'lib/networking/endpoints';
import { purple1, spinnerOverlayGrey } from 'lib/css/colors';
import Container from 'components/shared/Container';
import { filterContainerBoarder } from 'lib/css/colors';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import ProductsLinksSuggestionsModal from 'components/global_products/global_products_view/ProductsLinksSuggestionsModal';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import usePost from 'lib/hooks/usePost';
import useGet from 'lib/hooks/useGet';
import VerificationStatusIcon from 'components/global_products/detail_view/VerificationStatusIcon';

function ProductsLinksSuggestionsView() {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedVendor, setSelectedVendor] = useState({});
  const [selectedManufacturer, setSelectedManufacturer] = useState({});
  const [selectedBrand, setSelectedBrand] = useState({});
  const [selectedManufacturerProducts, setSelectedManufacturerProducts] = useState([]);
  const [selectedVendorProducts, setSelectedVendorProducts] = useState([]);
  const [selectedSimilarityScore, setSelectedSimilarityScore] = useState();
  const [showModal, setShowModal] = useState(false);

  const {
    data: { data } = [],
    brandsLoading,
    refetch: refetchBrands,
  } = useGet(BRANDS_FOR_DROPDOWN, {
    search: '',
    limit: 50,
    manufacturer_id: selectedManufacturer?.value,
  });

  const {
    data: { suggestions, count } = {},
    loading: loading,
    refetch: refetch,
  } = useGet(PRODUCT_LINKS_SUGGESTIONS_GET, {
    brand_id: selectedBrand?.value || null,
    global_manufacturer_id: selectedManufacturer?.value || null,
    global_vendor_id: selectedVendor?.value || null,
    min_similarity_score: selectedSimilarityScore || null,
    offset: currentPage - 1,
  });

  const { postData: linkProducts, loading: linkingProducts } = usePost(
    PRODUCT_LINKS_SUGGESTIONS_LINK_PRODUCTS,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'The selected products have been linked successfully',
        timer: 600,
      }).then(() => {
        refetch();
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred : ${error?.response?.data?.message}`,
      }),
  );

  const { postData: hideSuggestions } = usePost(
    PRODUCT_LINKS_SUGGESTIONS_HIDE_SUGGESTIONS,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'The selected suggestions have been hidden successfully',
        timer: 600,
      }).then(() => {
        refetch();
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred : ${error?.response?.data?.message}`,
      }),
  );

  const { postData: unclassifyProducts, loading: unclassifyingProducts } = usePost(
    GLOBAL_PRODUCT_REMOVE_CATEGORIES,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'The selected products have been unclassified successfully',
        timer: 600,
      }).then(() => {
        refetch();
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred : ${error?.response?.data?.message}`,
      }),
  );

  useEffect(() => {
    if (selectedManufacturer?.value) {
      refetchBrands();
    }
  }, [selectedManufacturer?.value, refetchBrands]);

  const manufacturerProducts = suggestions?.filter(
    suggestion => suggestion.manufacturer_product !== null,
  );

  // Deduplicate manufacturer products by ID
  const seenMfrIds = new Set();
  const uniqueManufacturerProducts = manufacturerProducts?.filter(suggestion => {
    const mfrProductId = suggestion.manufacturer_product?.id;
    if (mfrProductId && !seenMfrIds.has(mfrProductId)) {
      seenMfrIds.add(mfrProductId);
      return true;
    }
    return false;
  });

  const vendorProducts = suggestions
    ?.filter(suggestion => suggestion.vendor_product !== null)
    ?.map(suggestion => ({
      ...suggestion,
      similarity_score: suggestion.similarity_score || 0,
    }));

  const brandOptions = [...(data?.map(brand => ({ label: brand.name, value: brand.id })) || [])];

  const getProductImageUrl = (product, isVendorProduct = false) => {
    const productData = isVendorProduct ? product.vendor_product : product.manufacturer_product;
    return (
      productData.thumbnail_image?.url ||
      productData.propagated_thumbnail_image_url ||
      placeholderProductImage
    );
  };

  const handleLinkProducts = () => {
    if (selectedManufacturerProducts.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Manufacturer Product Selected',
        text: 'Please select a manufacturer product to link with vendor products.',
      });
      return;
    }

    areYouSure(
      () => {
        linkProducts({
          primary_manufacturer_product_id: selectedManufacturerProducts[0].manufacturer_product.id,
          vendor_product_ids_to_merge: selectedVendorProducts.map(
            product => product.vendor_product.id,
          ),
          ids_to_hide: [...selectedVendorProducts.map(product => product.id)],
        });
        setSelectedManufacturerProducts([]);
        setSelectedVendorProducts([]);
      },
      'Are you sure you want to link the selected products?',
      'Link Products',
    );
  };

  const handleHideSuggestions = () => {
    areYouSure(
      () => {
        hideSuggestions({
          ids_to_hide: [...selectedVendorProducts.map(product => product.id)],
        });
        setSelectedManufacturerProducts([]);
        setSelectedVendorProducts([]);
      },
      'Are you sure you want to hide the selected suggestions?',
      'Hide suggestions',
    );
  };

  const handleUnclassifyProducts = () => {
    areYouSure(
      () => {
        unclassifyProducts({
          product_ids: [
            ...selectedManufacturerProducts.map(
              product => product.manufacturer_product.global_product.id,
            ),
            ...selectedVendorProducts.map(product => product.vendor_product.global_product.id),
          ],
        });
      },
      'Are you sure you want to unclassify the selected products?',
      'Unclassify products',
    );
  };

  return (
    <Container>
      <div className="mb-3 d-flex justify-content-left">
        <h3 className="font-weight-bold mr-2">Suggestive Product Links</h3>
      </div>
      <Row>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Distributor</Form.Label>
            <SearchableScrollableSelect
              initOption={selectedVendor}
              onChange={option => {
                setSelectedVendor(option);
                setCurrentPage(1);
              }}
              fetchUrl={GLOBAL_VENDORS_FOR_DROPDOWN}
              optionMapper={vendor => ({ label: vendor.name, value: vendor.id })}
              autoFocus={false}
              disabled={selectedManufacturer?.value}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Manufacturer</Form.Label>
            <SearchableScrollableSelect
              initOption={selectedManufacturer}
              onChange={option => {
                setSelectedManufacturer(option);
                setCurrentPage(1);
              }}
              fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
              optionMapper={manufacturer => ({ label: manufacturer.name, value: manufacturer.id })}
              autoFocus={false}
              disabled={selectedVendor?.value}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Brand</Form.Label>
            <Select
              value={brandOptions?.find(option => option.value === selectedBrand)}
              options={brandOptions}
              onChange={option => {
                setSelectedBrand(option ? option : null);
                setCurrentPage(1);
              }}
              isLoading={brandsLoading}
              isClearable
              isDisabled={!selectedManufacturer?.value}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Min Similarity Score</Form.Label>
            <Form.Control
              type="number"
              placeholder="0.0 - 1.0"
              min="0"
              max="1"
              step="0.1"
              value={selectedSimilarityScore || ''}
              onChange={e => {
                const value = e.target.value;
                setSelectedSimilarityScore(value ? parseFloat(value) : null);
                setCurrentPage(1);
              }}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>&nbsp;</Form.Label>
            <div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setSelectedVendor({});
                  setSelectedManufacturer({});
                  setSelectedBrand({});
                  setSelectedSimilarityScore(null);
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </Button>
            </div>
          </Form.Group>
        </Col>
      </Row>
      {loading || linkingProducts || unclassifyingProducts ? (
        <div className={css(styles.spinnerOverlay)}>
          <LoadingSpinner />
        </div>
      ) : suggestions && suggestions.length > 0 ? (
        <>
          <Col className={classNames(css(styles.filterContainer), 'my-2 p-3')}>
            <Row>
              <Col>
                <Button
                  className={css(styles.button)}
                  variant="link"
                  size="lg"
                  disabled={currentPage === 1}
                  onClick={() => {
                    setCurrentPage(currentPage - 1);
                    setSelectedManufacturerProducts([]);
                    setSelectedVendorProducts([]);
                  }}
                >
                  &lt; Previous
                </Button>
                <Button
                  className={css(styles.button)}
                  variant="link"
                  size="lg"
                  disabled={currentPage === count}
                  onClick={() => {
                    setCurrentPage(currentPage + 1);
                    setSelectedManufacturerProducts([]);
                    setSelectedVendorProducts([]);
                  }}
                >
                  Next &gt;
                </Button>
                <span>{currentPage + '/' + count}</span>
              </Col>
              <Col className="d-flex justify-content-end align-content-center">
                <Button
                  className={css(styles.linkButton)}
                  variant="primary"
                  size="lg"
                  onClick={handleLinkProducts}
                  disabled={
                    selectedManufacturerProducts.length !== 1 || selectedVendorProducts.length < 1
                  }
                >
                  Link products
                </Button>
                <Button
                  className={css(styles.hideSuggestionButton)}
                  variant="outline-primary"
                  size="lg"
                  onClick={handleHideSuggestions}
                  disabled={selectedVendorProducts.length < 1}
                >
                  Hide suggestion
                </Button>
                <Button
                  className={css(styles.hideSuggestionButton)}
                  variant="outline-primary"
                  size="lg"
                  onClick={handleUnclassifyProducts}
                  disabled={
                    selectedVendorProducts.length < 1 && selectedManufacturerProducts.length < 1
                  }
                >
                  Unclassify products
                </Button>
              </Col>
            </Row>
            <Row>
              {uniqueManufacturerProducts && uniqueManufacturerProducts.length > 0 ? (
                <>
                  <span className="font-weight-bold ml-4 mb-2">Manufacturer Products</span>
                  <Table className="ml-4 mr-4" striped bordered hover>
                    <thead>
                      <tr>
                        <th width="5px">
                          <Form.Check
                            type="checkbox"
                            checked={
                              uniqueManufacturerProducts?.length > 0 &&
                              selectedManufacturerProducts.length ===
                                uniqueManufacturerProducts.length
                            }
                            onChange={e => {
                              setSelectedManufacturerProducts(
                                e.target.checked ? uniqueManufacturerProducts : [],
                              );
                            }}
                          />
                        </th>
                        <th width="230px">Manufacturer SKU</th>
                        <th width="450px">Name</th>
                        <th width="150px">GTIN</th>
                        <th width="200">Brand</th>
                        <th width="200px">Manufacturer</th>
                        <th width="200px">L4 Category</th>
                        <th width="100px">Pack Size</th>
                        <th width="100px">FSA Score</th>
                        <th width="50px">Verification Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uniqueManufacturerProducts.map(product => (
                        <tr key={product.id} className="ml-2 mb-2">
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={selectedManufacturerProducts.includes(product)}
                              onChange={e => {
                                if (e.target.checked) {
                                  setSelectedManufacturerProducts(prev => [...prev, product]);
                                } else {
                                  setSelectedManufacturerProducts(prev =>
                                    prev.filter(p => p.id !== product.id),
                                  );
                                }
                              }}
                            />
                          </td>
                          <td>
                            {product.manufacturer_product.global_product_id}
                            {' | '}
                            <a
                              href={
                                '/global-product-detail/' +
                                product.manufacturer_product.global_product.id +
                                '/' +
                                product.manufacturer_product.id +
                                '/manufacturer'
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: purple1 }}
                            >
                              {product.manufacturer_product?.manufacturer_sku}
                            </a>
                          </td>
                          <td>
                            <Row>
                              <Col sm={2}>
                                <OverlayTrigger
                                  placement="right"
                                  overlay={
                                    <Popover id="popover-basic" style={{ maxWidth: 400 }}>
                                      <Popover.Content>
                                        <Image
                                          width={400}
                                          fluid
                                          src={getProductImageUrl(product)}
                                        />
                                      </Popover.Content>
                                    </Popover>
                                  }
                                >
                                  <Image
                                    width={50}
                                    fluid
                                    src={getProductImageUrl(product)}
                                    thumbnail
                                  />
                                </OverlayTrigger>
                              </Col>
                              <Col>{product.manufacturer_product?.name}</Col>
                            </Row>
                          </td>
                          <td>{product.manufacturer_product?.gtin || '-'}</td>
                          <td>{product.manufacturer_product?.brand?.name || '-'}</td>
                          <td>{product.manufacturer_product?.global_manufacturer?.name || '-'}</td>
                          <td>
                            {product.manufacturer_product?.global_product?.l4_category
                              ?.category_name || '-'}
                          </td>
                          <td>{product.manufacturer_product?.pack_size || '-'}</td>
                          <td>{product.manufacturer_product?.fsa_score || '-'}</td>
                          <td>
                            <VerificationStatusIcon
                              verificationStatus={product.manufacturer_product?.verification_status}
                              aiSuggestedVerification={false}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              ) : (
                <span className="ml-4 mb-2">No manufacturer products in the cluster</span>
              )}
            </Row>
            <Row>
              {vendorProducts && vendorProducts.length > 0 ? (
                <>
                  <span className="font-weight-bold ml-4 mb-2">Vendor Products</span>
                  <Table className="ml-4 mr-4" striped bordered hover>
                    <thead>
                      <tr>
                        <th width="5px">
                          <Form.Check
                            type="checkbox"
                            checked={
                              vendorProducts?.length > 0 &&
                              selectedVendorProducts.length === vendorProducts.length
                            }
                            onChange={e => {
                              setSelectedVendorProducts(e.target.checked ? vendorProducts : []);
                            }}
                          />
                        </th>
                        <th width="230px">Vendor SKU</th>
                        <th width="450px">Name</th>
                        <th width="150px">GTIN</th>
                        <th width="200px">Brand</th>
                        <th width="200px">Manufacturer</th>
                        <th width="200px">L4 Category</th>
                        <th width="100px">Pack Size</th>
                        <th width="100px">FSA Score</th>
                        <th width="50px">Verification Status</th>
                        <th width="50px">Similarity Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendorProducts.map(product => (
                        <tr key={product.id} className="ml-2 mb-2">
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={selectedVendorProducts.some(p => p.id === product.id)}
                              onChange={e => {
                                if (e.target.checked) {
                                  setSelectedVendorProducts(prev => [...prev, product]);
                                } else {
                                  setSelectedVendorProducts(prev =>
                                    prev.filter(p => p.id !== product.id),
                                  );
                                }
                              }}
                            />
                          </td>
                          <td>
                            {product.vendor_product.global_product_id}
                            {' | '}
                            <a
                              href={
                                '/global-product-detail/' +
                                product.vendor_product.global_product.id +
                                '/' +
                                product.vendor_product.id +
                                '/distributor'
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: purple1 }}
                            >
                              {product.vendor_product.vendor_sku}
                            </a>
                            <br />
                            <span className="text-muted">
                              {product.vendor_product.global_vendor.name}
                            </span>
                          </td>
                          <td>
                            <Row>
                              <Col sm={2}>
                                <OverlayTrigger
                                  placement="right"
                                  overlay={
                                    <Popover id="popover-basic" style={{ maxWidth: 400 }}>
                                      <Popover.Content>
                                        <Image
                                          width={400}
                                          fluid
                                          src={getProductImageUrl(product, true)}
                                        />
                                      </Popover.Content>
                                    </Popover>
                                  }
                                >
                                  <Image
                                    width={50}
                                    fluid
                                    src={getProductImageUrl(product, true)}
                                    thumbnail
                                  />
                                </OverlayTrigger>
                              </Col>
                              <Col>{product.vendor_product.name}</Col>
                            </Row>
                          </td>
                          <td>{product.vendor_product.gtin || '-'}</td>
                          <td>{product.vendor_product.brand?.name || '-'}</td>
                          <td>{product.vendor_product.brand?.global_manufacturer?.name || '-'}</td>
                          <td>
                            {unclassifyingProducts ? (
                              <LoadingSpinner />
                            ) : (
                              product.vendor_product.global_product?.l4_category?.category_name ||
                              '-'
                            )}
                          </td>
                          <td>{product.vendor_product.pack_size || '-'}</td>
                          <td>{product.vendor_product.fsa_score || '-'}</td>
                          <td>
                            <VerificationStatusIcon
                              verificationStatus={product.vendor_product?.verification_status}
                              aiSuggestedVerification={false}
                            />
                          </td>
                          <td>{product.similarity_score || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              ) : (
                <span className="ml-4 mb-2">No vendor products in the cluster</span>
              )}
            </Row>
          </Col>
        </>
      ) : (
        <span className="ml-4 mb-2">No suggestions to display</span>
      )}
      {showModal && (
        <ProductsLinksSuggestionsModal
          selectedManufacturerProducts={selectedManufacturerProducts}
          selectedVendorProducts={selectedVendorProducts}
          linkProducts={linkProducts}
          setSelectedManufacturerProducts={setSelectedManufacturerProducts}
          setSelectedVendorProducts={setSelectedVendorProducts}
          onHide={() => setShowModal(false)}
          getProductImageUrl={getProductImageUrl}
        />
      )}
    </Container>
  );
}

const styles = StyleSheet.create({
  filterContainer: {
    border: `3px solid ${filterContainerBoarder}`,
    marginLeft: '1px',
    marginRight: '1px',
    width: 'auto',
    minWidth: '100px',
    height: 'auto',
    minHeight: '70px',
    overflow: 'visible',
    position: 'relative',
  },
  button: {
    fontSize: '1.2em',
    padding: '0.5em',
    fontWeight: 'bold',
  },
  linkButton: {
    width: '100px',
    maxHeight: '30px',
    marginLeft: '4px',
  },
  hideSuggestionButton: {
    width: '150px',
    maxHeight: '30px',
    marginLeft: '4px',
  },
  largeModal: {
    width: '90%',
    maxWidth: '1200px',
    height: '80vh',
    maxHeight: '800px',
  },
  spinnerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: spinnerOverlayGrey,
    zIndex: 1,
  },
});

export default ProductsLinksSuggestionsView;
